import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCheck, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { Modal, Field, TextArea, Selecter } from "../pages/crm/crmui";
import { Btn } from "./common";

const FIELD_UI = {
  nif: { type: "text", label: "NIF" },
  morada: { type: "text", label: "Morada" },
  codigo_postal: { type: "text", label: "Codigo postal" },
  cidade: { type: "text", label: "Cidade" },
  email: { type: "text", label: "Email geral" },
  fatura_a_nome_de: { type: "text", label: "Faturar a nome de" },
  morada_faturacao: { type: "text", label: "Morada de faturacao" },
  contacto_faturacao_email: { type: "text", label: "Email de faturacao" },
  condicoes_pagamento: { type: "text", label: "Condicoes de pagamento" },
  rgpd_base_legal: {
    type: "select",
    label: "Base legal RGPD",
    options: [
      { value: "", label: "- Escolher -" },
      { value: "consentimento", label: "Consentimento" },
      { value: "contrato", label: "Execucao de contrato" },
      { value: "obrigacao_legal", label: "Obrigacao legal" },
      { value: "interesse_legitimo", label: "Interesse legitimo" },
    ],
  },
};

export default function ConvertToCustomerModal({ open, onClose, account, onDone }) {
  const [check, setCheck] = useState(null);
  const [patch, setPatch] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !account?.id) return;
    setPatch({});
    api.get(`/crm/accounts/${account.id}/convert-check`)
      .then(({ data }) => setCheck(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [open, account?.id]);

  const setF = (k, v) => setPatch((p) => ({ ...p, [k]: v }));

  const doConvert = async () => {
    if (!account?.id) return;
    setBusy(true);
    try {
      await api.post(`/crm/accounts/${account.id}/convert-to-customer`, patch);
      toast.success("Conta convertida em cliente.");
      onDone && onDone();
      onClose && onClose();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det && typeof det === "object" && det.em_falta) {
        toast.error(det.mensagem || "Faltam campos obrigatorios.");
        setCheck({ em_falta: det.em_falta, pode_converter: false, tipo_atual: account.tipo });
      } else {
        toast.error(formatApiErrorDetail(det));
      }
    } finally { setBusy(false); }
  };

  const faltantes = check?.em_falta || [];

  return (
    <Modal open={open} onClose={onClose} title="Converter em cliente" testid="convert-modal" size="lg">
      <div className="flex items-start gap-3 bg-blue/5 border border-blue/20 rounded-md px-3 py-2">
        <UserCheck size={18} className="text-blue mt-0.5" />
        <div className="text-xs text-slate-700">
          Ao converter <strong>{account?.nome || "esta conta"}</strong> em cliente, o sistema atribui um codigo <strong>CLI-YYYY-NNNN</strong> e valida os dados obrigatorios da ficha de cliente e faturacao. Uma atividade e uma entrada de auditoria sao criadas automaticamente.
        </div>
      </div>

      {check === null && <div className="text-sm text-muted2 py-4 text-center">A validar...</div>}

      {check && faltantes.length === 0 && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-2 text-sm">
          <UserCheck size={16} /> Todos os campos obrigatorios estao preenchidos.
        </div>
      )}

      {check && faltantes.length > 0 && (
        <>
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2 text-sm">
            <AlertCircle size={16} /> Preencha os seguintes campos para poder converter:
          </div>
          <div className="grid grid-cols-2 gap-3">
            {faltantes.map((f) => {
              const ui = FIELD_UI[f.campo] || { type: "text", label: f.label };
              if (ui.type === "select") {
                return (
                  <Selecter key={f.campo} label={ui.label} value={patch[f.campo] || ""} onChange={(v) => setF(f.campo, v)} options={ui.options} testid={`convert-${f.campo}`} />
                );
              }
              return (
                <Field key={f.campo} label={ui.label} value={patch[f.campo] || ""} onChange={(v) => setF(f.campo, v)} testid={`convert-${f.campo}`} />
              );
            })}
          </div>
        </>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
        <Btn onClick={doConvert} testid="convert-confirm" disabled={busy}>{busy ? "A converter..." : "Converter em cliente"}</Btn>
      </div>
    </Modal>
  );
}
